import { Play, Pause, X, Volume2, VolumeX } from "lucide-react";
import { usePodcastPlayer } from "@/context/PodcastPlayerContext";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/i18n/useLocale";

const PersistentPodcastPlayer = () => {
  const t = useT();
  const {
    currentEpisode,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    pause,
    resume,
    seek,
    setVolume,
    toggleMute,
    setPlaybackRate,
    stopPlayback,
  } = usePodcastPlayer();

  const { open: sidebarOpen } = useSidebar();

  if (!currentEpisode) return null;

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const handleSeek = (value: number[]) => {
    seek(value[0]);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
  };

  const handleSpeedChange = () => {
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackRate(nextSpeed);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Collapsed state (when sidebar is closed)
  if (!sidebarOpen) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleTogglePlay}
              size="icon"
              aria-label={isPlaying ? "Pause podcast" : "Play podcast"}
              className="relative w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
              <div
                className="absolute inset-0 rounded-full border-2 border-primary"
                style={{
                  background: `conic-gradient(hsl(var(--primary)) ${progress}%, transparent ${progress}%)`,
                  clipPath: "inset(0 round 9999px)",
                }}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">{currentEpisode.title}</p>
            <p className="text-xs text-muted-foreground">{currentEpisode.podcast?.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded state (when sidebar is open)
  return (
    <div className="bg-background/95 backdrop-blur-sm border-t border-border/50 p-3 space-y-3">
      {/* Episode Info */}
      <div className="flex items-start gap-3">
        <img
          src={currentEpisode.image_url || currentEpisode.podcast?.image_url || undefined}
          alt={currentEpisode.title}
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium line-clamp-1">{currentEpisode.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-1">
            {currentEpisode.podcast?.name}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("auto.stop-playback-d968")}
          className="h-8 w-8 flex-shrink-0"
          onClick={stopPlayback}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1">
        <Slider
          value={[currentTime]}
          max={duration || 100}
          step={1}
          onValueChange={handleSeek}
          className="cursor-pointer"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={isMuted ? "Unmute" : "Mute"}
            className="h-8 w-8"
            onClick={toggleMute}
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
          <Slider
            value={[volume]}
            max={1}
            step={0.01}
            onValueChange={handleVolumeChange}
            className="w-16"
          />
        </div>

        <Button
          onClick={handleTogglePlay}
          size="icon"
          aria-label={isPlaying ? "Pause" : "Play"}
          className="h-10 w-10 rounded-full"
        >
          {isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleSpeedChange}
          className="h-8 px-2 text-xs font-medium"
        >
          {playbackRate}x
        </Button>
      </div>
    </div>
  );
};

export default PersistentPodcastPlayer;
